import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  useCopyToWorld,
  useTaxonomyCategories,
  useTaxonomyEntries,
  useTaxonomyGroups,
  type TaxonomyCategoryDto,
} from '../../api/queries/taxonomy';
import { TaxonomyEntryCard } from './TaxonomyEntryCard';

const PAGE_SIZE = 24;

interface Props {
  open: boolean;
  onClose: () => void;
  worldId: string;
}

export function ImportFromLibraryDialog({ open, onClose, worldId }: Props) {
  const { data: groups = [] } = useTaxonomyGroups();
  const { data: allCategories = [] } = useTaxonomyCategories();
  const copyToWorld = useCopyToWorld();

  const [selectedCategoryKey, setSelectedCategoryKey] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const { data: entriesResult, isLoading } = useTaxonomyEntries({
    categoryKey: selectedCategoryKey || undefined,
    search: search.trim() || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const entries = entriesResult?.items ?? [];
  const totalPages = entriesResult?.totalPages ?? 0;

  const categoriesByGroup = useMemo(() => {
    const map = new Map<string, TaxonomyCategoryDto[]>();
    for (const cat of allCategories) {
      const list = map.get(cat.groupKey) ?? [];
      list.push(cat);
      map.set(cat.groupKey, list);
    }
    return map;
  }, [allCategories]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleImport = async () => {
    if (selectedIds.size === 0) return;
    await copyToWorld.mutateAsync({
      worldId,
      entryIds: Array.from(selectedIds),
    });
    setSelectedIds(new Set());
    onClose();
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSelectedCategoryKey('');
    setSearch('');
    setPage(1);
    onClose();
  };

  const selectedCategory = allCategories.find(
    (c) => c.key === selectedCategoryKey,
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import from Reference Library</DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', height: 500 }}>
        {/* Mini sidebar */}
        <Box
          sx={{
            width: 220,
            minWidth: 220,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          <List dense disablePadding>
            {groups.map((group) => {
              const cats = categoriesByGroup.get(group.key) ?? [];
              const isCollapsed = collapsedGroups.has(group.key);
              return (
                <Box key={group.key}>
                  <ListItemButton
                    onClick={() => toggleGroup(group.key)}
                    sx={{ px: 2, py: 0.25 }}
                  >
                    <ListItemText
                      primary={group.name}
                      primaryTypographyProps={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                      }}
                    />
                    {isCollapsed ? (
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ExpandLessIcon sx={{ fontSize: 16 }} />
                    )}
                  </ListItemButton>
                  <Collapse in={!isCollapsed}>
                    {cats.map((cat) => (
                      <ListItemButton
                        key={cat.key}
                        selected={cat.key === selectedCategoryKey}
                        onClick={() => {
                          setSelectedCategoryKey(cat.key);
                          setPage(1);
                        }}
                        sx={{ pl: 3, pr: 1, py: 0.25 }}
                      >
                        <ListItemText
                          primary={cat.name}
                          primaryTypographyProps={{
                            fontSize: 12,
                            noWrap: true,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {cat.entryCount}
                        </Typography>
                      </ListItemButton>
                    ))}
                  </Collapse>
                </Box>
              );
            })}
          </List>
        </Box>

        {/* Grid */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ p: 2, pb: 1 }}
            alignItems="center"
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              {selectedCategory?.name ?? 'Select a category'}
            </Typography>
            {selectedCategoryKey && (
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                sx={{ width: 220 }}
              />
            )}
          </Stack>

          <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
            {!selectedCategoryKey && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a category to browse entries
                </Typography>
              </Box>
            )}

            {isLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 1.5,
              }}
            >
              {entries.map((entry) => (
                <TaxonomyEntryCard
                  key={entry.id}
                  entry={entry}
                  selected={selectedIds.has(entry.id)}
                  onToggleSelect={toggleSelect}
                  multiSelectMode={true}
                />
              ))}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: 1, pl: 2 }}
        >
          {selectedIds.size > 0
            ? `${selectedIds.size} selected`
            : 'Click entries to select them'}
        </Typography>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          disabled={selectedIds.size === 0 || copyToWorld.isPending}
          onClick={handleImport}
        >
          {copyToWorld.isPending
            ? 'Importing...'
            : `Import ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
